import { FiEdit, FiPhone, FiMail } from "react-icons/fi";
import { LuTrash2 } from "react-icons/lu";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { modalActions } from "../../store/modalSlice";
import { userActions } from "../../store/userSlice";
import { GoVerified } from "react-icons/go";
import { MdVerified } from "react-icons/md";
import { CiUser } from "react-icons/ci";
import { useState } from "react";

export const tBodyItemStyle = {
    padding: '12px 20px',
}

export const tBodyRowStyle = {
    height: '72px',
    cursor: 'pointer'
}

function capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function timeAgo(dateStr) {
    if (!dateStr) return '—';
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    if (isNaN(diffMs) || diffMs < 0) return '—';
    const diffSeconds = Math.floor(diffMs / 1000);
    if (diffSeconds < 60) return 'Just now';
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
}

// Mobile Card Component
export function UserListItemMobile({ user }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const handleCardClick = () => {
        navigate(`/users/${user.id}/edit`);
        dispatch(userActions.fillUser({ user: user }));
    };

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        dispatch(modalActions.openModal({
            details: {
                id: user.id,
                name: user.user_type === 'individual' ? `${user.first_name} ${user.last_name}` : user.company_name,
                category: 'user'
            }
        }));
    };

    const userName = user.user_type === 'individual'
        ? `${user.first_name} ${user.last_name}`
        : user.company_name;

    const phoneNumber = user.user_type === 'individual'
        ? user.phone_number
        : user.contact_number;

    return (
        <div
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 cursor-pointer active:bg-gray-50 dark:active:bg-gray-700"
            onClick={handleCardClick}
        >
            {/* Top section: Avatar + Info */}
            <div className="flex gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    {user.profile_image ? (
                        <div className="relative w-12 h-12">
                            {!isImageLoaded && (
                                <div className="absolute rounded-full bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 top-0 left-0 w-full h-full animate-pulse" />
                            )}
                            <img
                                src={`https://api.pronascout.com/${user.profile_image}?v=${Date.now()}`}
                                alt="profile"
                                className={`w-12 h-12 rounded-full object-cover transition-opacity duration-300 ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                                onLoad={() => setIsImageLoaded(true)}
                                onError={() => setIsImageLoaded(true)}
                            />
                        </div>
                    ) : (
                        <div className="w-12 h-12 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center bg-gray-50 dark:bg-gray-700">
                            <CiUser size={24} className="text-gray-400" />
                        </div>
                    )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{userName}</h3>
                        {user.isVerified === 1 && <MdVerified size={16} className="text-primary flex-shrink-0" />}
                    </div>
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        {capitalizeFirstLetter(user.user_type)}
                    </span>
                    <div className="flex flex-col gap-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {phoneNumber && (
                            <div className="flex items-center gap-1.5">
                                <FiPhone size={12} />
                                <span className="truncate">{phoneNumber}</span>
                            </div>
                        )}
                        {user.email && (
                            <div className="flex items-center gap-1.5">
                                <FiMail size={12} />
                                <span className="truncate">{user.email}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Bottom section: Actions */}
            <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-400">{user.created_at}</span>
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">{user.listings_count || 0} listings</span>
                </div>
                <div className="flex gap-2">
                    <button
                        className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"
                        title="Edit"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <FiEdit size={14} />
                    </button>
                    <button
                        className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"
                        title="Delete"
                        onClick={handleDeleteClick}
                    >
                        <LuTrash2 size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function UserListItem({ user }) {

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const [isImageLoaded, setIsImageLoaded] = useState(false)

    const handleRowClick = () => {
        navigate(`/users/${user.id}/edit`)
        dispatch(userActions.fillUser({ user: user }))
    }

    const handleUserEditClick = (e) => {
        // e.stopPropagation();
        // dispatch(userActions.fillUser({ user: user }))
    }

    const handleUserDeleteClick = (e) => {
        e.stopPropagation();
        dispatch(modalActions.openModal(
            {
                details:
                {
                    id: user.id,
                    name: user.user_type === 'individual' ? `${user.first_name} ${user.last_name}` : user.company_name,
                    category: 'user'
                }
            }))
    }

    return (
        <>
            <tr className="table_row" onClick={handleRowClick}>
                <td style={tBodyItemStyle} className="text-black dark:text-gray-100 font-medium w-[25%]">
                    <div className="flex gap-4 items-center">
                        {user.profile_image ? (
                            <div className="relative w-8 h-8">
                                {!isImageLoaded && (
                                    <div className="absolute rounded-full bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 top-0 left-0 w-full h-full transition-opacity duration-300"
                                        style={{
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s infinite'
                                        }}
                                    />
                                )}
                                <img
                                    src={`https://api.pronascout.com/${user.profile_image}?v=${Date.now()}`}
                                    alt="profile"
                                    className={`w-8 h-8 rounded-full object-cover ${!isImageLoaded ? 'opacity-0' : 'opacity-100'
                                        } transition-opacity duration-300`}
                                    onLoad={() => setIsImageLoaded(true)}
                                    onError={() => setIsImageLoaded(true)}
                                />
                            </div>
                        ) : (
                            <div className="w-8 h-8 rounded-full border border-gray-300 dark:border-gray-600 flex items-center justify-center">
                                <CiUser size={18} color="#888" />
                            </div>
                        )}

                        {user.first_name && (
                            <div className="flex items-center gap-2">
                                <span className="line-clamp-1">{user.first_name} {user.last_name}</span>
                                {user.isVerified === 1 && <span><MdVerified size={18} title="Verified" color="var(--primary-color)" /></span>}
                            </div>
                        )}
                        {user.company_name && (
                            <div className="flex items-center gap-2">
                                <span className="line-clamp-1">{user.company_name}</span>
                                {user.isVerified === 1 && <span><MdVerified size={18} title="Verified" color="var(--primary-color)" /></span>}
                            </div>
                        )}
                        {/* Agents have neither an individuals nor a companies row —
                            their display name lives on agents.name. */}
                        {!user.first_name && !user.company_name && (
                            <span className="line-clamp-1">{user.agent_name || user.email || '—'}</span>
                        )}
                        {user.isVerified === 0 && (
                            <span
                                title="E-mail code never entered — the account cannot log in"
                                className="inline-flex flex-shrink-0 items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            >
                                Unverified
                            </span>
                        )}
                        {(user.status === 'suspended' || user.agent_status === 'suspended') && (
                            <span
                                title={user.status === 'suspended' ? 'Suspended by an admin' : 'Suspended by their agency'}
                                className="inline-flex flex-shrink-0 items-center rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700 dark:bg-orange-900/40 dark:text-orange-300"
                            >
                                Restricted
                            </span>
                        )}
                    </div>
                </td>
                <td style={tBodyItemStyle} className="text-black dark:text-gray-100 font-medium w-[20%]">{user.created_at || '—'}</td>
                <td style={tBodyItemStyle} className="w-[15%] dark:text-gray-300">
                    {user.user_type === 'individual' && user.phone_number}
                    {user.user_type === 'company' && user.contact_number}
                </td>
                <td style={tBodyItemStyle} className="w-[20%] dark:text-gray-300">{user.email}</td>
                <td style={tBodyItemStyle} className="w-[10%] dark:text-gray-300">{capitalizeFirstLetter(user.user_type)}</td>
                <td style={tBodyItemStyle} className="w-[8%] text-center dark:text-gray-300">
                    <span className={`inline-flex items-center justify-center min-w-[28px] px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        (user.listings_count || 0) > 0
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                        {user.listings_count || 0}
                    </span>
                </td>
                <td style={tBodyItemStyle} className="w-[10%] text-xs text-gray-500 dark:text-gray-400">
                    {user.last_active ? new Date(user.last_active).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'}
                </td>
                <td style={tBodyItemStyle} className="w-[15%]">
                    <div className="flex justify-center gap-4">
                        <div className="rounded-full w-8 h-8 text-center bg-primary relative cursor-pointer flex justify-center actions" title="Edit" onClick={handleUserEditClick}>
                            <FiEdit style={{ fontSize: '14px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                        </div>
                        <div className="rounded-full w-8 h-8 flex justify-center items-center text-center bg-primary relative cursor-pointer actions" title="Delete" onClick={handleUserDeleteClick}>
                            <LuTrash2 style={{ fontSize: '16px', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                        </div>
                    </div>
                </td>
            </tr>
        </>
    )
}